import { Content } from "pdfmake/interfaces";
import { CompanyType, loadImage, resizeLogo } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import {
  emptyHospitalContent,
  HospitalAddressMap,
  OADetailType,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import HeaderSapiensTexInvoiceForm from "react-lib/apps/HISV3/DPO/sequence/HeaderSapiensTexInvoiceForm";

import CONFIG from "config/config";

const color = {
  greyHeader: "#D9D9D9",
  greySummary: "#E8E8E8",
  greyBorder: "#9f9f9f",
  pink: "#F382A0",
  blue: "#095e9d",
};

export type ReceiptType = "refund" | "deposit" | "receipt";

type FormSapiensTaxInvoiceType = {
  name?: any;
  code?: string;
  created?: string;
  hn?: string;
  // data
  fields?: Record<string, any>[];
  // summary
  totalAmount?: any;
  patientNameEN?: string;
  price_text?: string;
  remark?: string;
  cashier?: string;
  isWatermark?: boolean;
  payment_type?: string;
  receiptType?: string;
  wallet_deposit_code?: string;
  cancelled_code?: string;
  division?: string;
  pageMargins?: any;
  isPayment?: boolean;
  isShowBackground?: boolean;
  priceTextEN?: string;
  oaDetail?: OADetailType;
  price?: string;
  discountAmount?: any;
  amountAfterDiscount?: any;
  vatAmount?: any;
  grandTotal?: any;
};

const isMEHealth = CONFIG.COMPANY === "MEHEALTH";
const meHealthDivision = ["MeHealth", "MeHealth Medical Lab"];

const extractHn = (hn: string): string => {
  if (!hn) return "";

  const parts = hn.split("_");

  return parts[parts.length - 1];
};

const FormSapiensTaxInvoice = async (props: FormSapiensTaxInvoiceType) => {
  var origin = window?.location?.origin;

  const getHospitalAddress = (company: CompanyType, oaDetail?: OADetailType): Content => {
    return HospitalAddressMap[company]?.(oaDetail, props?.division, true) ?? emptyHospitalContent;
  };

  const isMeHealthDivision = (division?: string) => meHealthDivision.includes(division || "");

  const oaImageFile = isMeHealthDivision(props?.division)
    ? `${origin}/static/images/meBilling/venture_catalyst_logo.jpg`
    : props?.oaDetail?.imageFile;

  const headerSapiensForm = await HeaderSapiensTexInvoiceForm({
    form: "FormReceipt",
    images: {
      oaLogo: oaImageFile || "",
    },
    logoHight:
      CONFIG.COMPANY === "MSK"
        ? 40
        : CONFIG.COMPANY === "RAMASRI"
          ? 45
          : CONFIG.COMPANY === "MEHEALTH"
            ? 45
            : 24,
    pageMargins: [12, 195, 12, 282],
    hospitalAddress: getHospitalAddress(CONFIG.COMPANY as CompanyType, props.oaDetail),
    receiptType: props.receiptType,
    isWatermark: props.isWatermark,
    wallet_deposit_code: props.wallet_deposit_code,
    cancelled_code: props.cancelled_code,
    code: props.code,
    created: props.created,
    header: {
      stack: [headerMeBilling(props)],
    },
    styles: {
      receiptHeader: {
        bold: true,
        fontSize: 8,
      },
      receiptInfo: {
        fontSize: 8,
      },
      receiptHeaderNumber: {
        bold: true,
        fontSize: 8,
        color: isMEHealth ? color.pink : color.blue,
      },
    },
  });

  const fields = props.fields ?? [];
  const remainder = fields.length % 10;
  const numberRows = remainder === 0 ? 0 : 10 - remainder;

  const createRightCell = (value: string | number = "") =>
    tableCell([tableText(String(value), "KanitLM"), tableText("\n")], {
      alignment: "right",
    });

  const fileData = fields.map((item: any) => {
    return [
      tableCell([tableText(item?.item_name, "KanitLM"), tableText(item?.item_name_en, "KanitLM")], {
        margin: [10, 0, 0, 5],
      }),

      tableCell([tableText(String(item?.quantity), "KanitLM"), tableText("\n")], {
        alignment: "center",
      }),
      createRightCell(formatMoney(item?.paymentUnit || 0)),
      createRightCell(formatMoney(item?.paymentTotal || 0)),
    ];
  });

  const rowEmpty = Array.from({ length: numberRows }).map(() => [
    {
      stack: [
        { text: " ", font: "KanitLM", style: "tableData" },
        { text: " ", font: "PoppinsLM", style: "tableData" },
      ],
      margin: [10, 0, 0, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
  ]);

  let resized = { width: 400 };

  if (oaImageFile) {
    const img = await loadImage(oaImageFile);
    resized = resizeLogo(img.width, img.height, 280);
  }

  const shouldShowLogo =
    (props.isPayment || props.isShowBackground) &&
    !CONFIG.HIDE_COMPANY_LOGO_PRINT &&
    headerSapiensForm.images?.logo;

  const watermarkBackground = shouldShowLogo
    ? {
        image: "logo",
        opacity: 0.18,
        width: oaImageFile ? resized.width : 400,
        alignment: "center",
        absolutePosition: { y: oaImageFile ? 290 : 400 },
      }
    : {};

  const imgSignatureVenture = await loadImage(
    `${origin}/static/images/meBilling/signature_venture.png`,
  );

  const resizedSignatureVenture = resizeLogo(
    imgSignatureVenture.width,
    imgSignatureVenture.height,
    20,
  );

  return {
    pageSize: "A4",
    ...headerSapiensForm,
    watermark: {
      text: props.isWatermark ? "สำเนา/ Copy" : "",
      font: "KanitLM",
      opacity: 0.2,
      bold: true,
      angle: -15,
      fontSize: 70,
    },
    background: [watermarkBackground],
    content: [
      {
        margin: [12, -11, 12, 0],
        stack: [
          {
            table: {
              headerRows: 2,
              widths: [315, "*", "*", "*"],
              body: [
                [
                  {
                    text: [
                      {
                        text: "รายการ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Description",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    position: "absolute",
                    relativePosition: { y: 14 },
                    rowSpan: 2,
                  },
                  {
                    text: [
                      {
                        text: "จำนวนเงิน(บาท) ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Amount (Baht)",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    colSpan: 3,
                  },
                  {},
                  {},
                ],
                [
                  {},
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวน" : "จำนวนเงิน",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Qty)" : "(Total Amount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวนเงินต่อหน่วย" : "ส่วนลด",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Amount/Unit)" : "(Discount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวนเงินรวม" : "จำนวนเงินสุทธิ",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Total Amount)" : "(Net Amount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                ],
                ...fileData,
                ...rowEmpty,
              ],
            },
            layout: layoutTable,
          },
        ],
      },
    ],
    styles: {
      ...headerSapiensForm.styles,
      tableData: { fontSize: 8 },
      tableDataPoppins: { fontSize: 6 },
      tableHeaderSummary: {
        bold: true,
        fontSize: 8,
      },
      footerSignature: { fontSize: 9 },
      footerData: { bold: true, fontSize: 6 },
    },
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [24, 0, 24, 0],
        stack: footerMeBilling(props, resizedSignatureVenture),
      };
    },
    images: {
      ...headerSapiensForm.images,
      background: origin + "/static/images/logoform/SAPIENS/HeaderSapiensForm.png",
      signatureVenture: origin + "/static/images/meBilling/signature_venture.png",
    },
  };
};

export default FormSapiensTaxInvoice;

/*================================ HANDLES RECEIPT ==============================*/

export const receiptMap = {
  refund: {
    th: "ใบคืนเงินมัดจำ",
    en: "Deposit Refund",
  },
  receipt: {
    th: "ใบเสร็จรับเงิน",
    en: "Receipt",
  },
  deposit: {
    th: "ใบรับเงินมัดจำ",
    en: "Deposit",
  },
};

export const copyMap = {
  watermark: {
    th: "สำเนา ",
    en: "(Copy)",
  },
  original: {
    th: "ต้นฉบับ ",
    en: "(Original)",
  },
} as const;

export const receiptNoMap = {
  deposit: {
    label: "/Doc No.  ",
    code: (props: FormSapiensTaxInvoiceType) => props.wallet_deposit_code || "",
  },
  refund: {
    label: "/Doc No.  ",
    code: (props: FormSapiensTaxInvoiceType) => props.cancelled_code || "",
  },
  receipt: {
    label: "/Receipt No.  ",
    code: (props: FormSapiensTaxInvoiceType) => props.code || "",
  },
};

const headerLabelCell = (th: string, en: string) => ({
  text: [
    { text: th, font: "KanitLM", style: "receiptHeader" },
    { text: en, font: "PoppinsLM", style: "receiptHeader" },
  ],
  alignment: "left",
  border: [false, false, false, false],
});

type HeaderValueType = "text" | "columns";

const headerValueCell = (data: any, type: HeaderValueType = "text") => ({
  alignment: "left",
  border: [false, false, false, false],
  margin: [0, 0, 0, 0],
  ...(type === "columns" ? { columns: data } : { text: data ?? "" }),
});

type TextItemOptions = {
  font?: string;
  style?: string;
  width?: string | number;
  margin?: number[];
};

const textItem = (text: string, options: TextItemOptions = {}) => ({
  text,
  font: options.font ?? "KanitLM",
  style: options.style ?? "receiptInfo",
  ...(options.width ? { width: options.width } : {}),
  ...(options.margin ? { margin: options.margin } : {}),
});

const headerText = (text: any[]) => headerValueCell(text, "text");

const headerMeBilling = (props: FormSapiensTaxInvoiceType) => {
  return [
    {
      margin: [0, 10, 0, 0],
      table: {
        widths: [95, "*"],
        body: [
          [
            headerLabelCell("รหัสลูกค้า ", "/ Customer ID "),
            headerText([textItem(`: ${extractHn(`${props.hn || ""}`)}`, { font: "PoppinsLM" })]),
          ],
          [
            headerLabelCell("ชื่อ ", "/ Name "),
            headerText([textItem(`: ${props.name} ${props.patientNameEN || ""}`)]),
          ],
        ],
      },
    },
  ];
};

/*================================ CONTENT RECEIPT ==============================*/

const tableCell = (
  content: any,
  options: {
    alignment?: "left" | "right" | "center";
    margin?: number[];
  } = {},
) => ({
  ...(Array.isArray(content) ? { stack: content } : { text: content }),
  alignment: options.alignment,
  margin: options.margin ?? [0, 0, 3, 5],
  border: [true, false, true, false],
});

const tableText = (text: string = " ", font: "KanitLM" | "PoppinsLM" = "PoppinsLM") => ({
  text,
  font,
  style: "tableData",
});

/*================================ FOOTER RECEIPT ==============================*/

const layoutTable = {
  hLineColor: () => color.greyBorder,
  vLineColor: () => color.greyBorder,
};

const thEn = (
  th: string,
  en: string,
  fontTh = "KanitLM",
  fontEn = "PoppinsLM",
  style = "tableHeaderSummary",
) => [
  { text: th, font: fontTh, style },
  { text: `/${en}`, font: fontEn, style },
];

const formatMoney = (value: any) => {
  const numberValue = Number(String(value ?? 0).replace(/,/g, ""));

  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const footerMeBilling = (
  props: FormSapiensTaxInvoiceType,
  signature: { width: number; height?: number },
) => {
  return [
    {
      table: {
        widths: [315, "*", "*", "*"],
        body: [
          [
            { text: "", border: [false, true, false, false] },
            {
              text: thEn("ยอดรวมเงิน ", "Subtotal"),
              border: [false, true, false, false],
              colSpan: 2,
            },
            {},
            {
              text: formatMoney(props.totalAmount),
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [true, true, true, true],
            },
          ],
          [
            { text: "", border: [false, false, false, false] },
            {
              text: thEn("หักส่วนลด ", "Discount"),
              border: [false, false, false, false],
              colSpan: 2,
            },
            {},
            {
              text: formatMoney(props.discountAmount),
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [true, true, true, true],
            },
          ],
          [
            { text: "", border: [false, false, false, false] },
            {
              text: thEn("จำนวนเงินหลังหักส่วนลด ", "Total"),
              border: [false, false, false, false],
              colSpan: 2,
            },
            {},
            {
              text: formatMoney(props.amountAfterDiscount),
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [true, true, true, true],
            },
          ],
          [
            { text: "", border: [false, false, false, false] },
            {
              text: thEn("จำนวนภาษีมูลค่าเพิ่ม ", "Vat"),
              border: [false, false, false, false],
              colSpan: 2,
            },
            {},
            {
              text: formatMoney(props.vatAmount),
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [true, true, true, true],
            },
          ],
          [
            { text: "", border: [false, false, false, false] },
            {
              text: thEn("จำนวนเงินรวมทั้งสิ้น ", "Grand Total"),
              border: [false, false, false, false],
              colSpan: 2,
            },
            {},
            {
              text: formatMoney(props.grandTotal),
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [true, true, true, true],
            },
          ],
          [
            {
              text: `${props.price_text} (${props.priceTextEN})`,
              margin: [0, 3, 0, 0],
              font: "KanitLM",
              style: "tableHeaderSummary",
              alignment: "right",
              border: [false, false, false, false],
              colSpan: 4,
            },
            {},
            {},
            {},
          ],
        ],
      },
      layout: layoutTable,
    },
    {
      table: {
        widths: [100, "*"],
        body: [
          [
            {
              text: thEn("ชำระเงินโดย ", "Payment"),
            },
            {
              text: `: ${props.payment_type}`,
              font: "KanitLM",
              style: "tableData",
            },
          ],
          [
            {
              text: [
                { text: "หมายเหตุ ", font: "KanitLM", style: "tableData" },
                { text: "/Remark", font: "Poppins", style: "tableData" },
              ],
            },
            {
              text: `: ${props.remark}`,
              font: "KanitLM",
              style: "tableData",
            },
          ],
        ],
      },
      layout: {
        hLineWidth: (i: number, node: any) => {
          return i === 1 ? 0.5 : 0;
        },
        hLineColor: () => color.greyBorder,
        hLineStyle: (i: number, node: any) => {
          return {
            dash: {
              length: 3,
              space: 3,
            },
          };
        },
        vLineWidth: () => 0,
      },
    },

    {
      table: {
        widths: ["*", 240],
        body: [
          [
            { text: "", border: [false, false, false, false] },
            {
              text: "ได้รับเงินไว้เป็นการถูกต้องแล้ว\n\n",
              font: "KanitLM",
              style: "footerSignature",
              alignment: "center",
              border: [false, false, false, false],
            },
          ],
          [
            {
              stack: [
                {
                  text: "ลงชื่อ/Signature ____________________",
                  font: "KanitLM",
                  style: "footerSignature",
                  alignment: "left",
                  margin: [45, 0, 0, 0],
                },
                {
                  text: `(          ผู้รับบริการ/Customer        )`,
                  font: "KanitLM",
                  style: "footerSignature",
                  alignment: "center",
                },
              ],
              border: [false, false, false, false],
            },
            {
              stack: [
                {
                  columns: [
                    {
                      text: "ลงชื่อ/Signature",
                      font: "KanitLM",
                      style: "footerSignature",
                      width: "auto",
                      margin: [0, -20, 0, 0],
                    },

                    {
                      stack: [
                        {
                          image: "signatureVenture",
                          width: signature.width,
                          alignment: "center",
                          margin: [0, -30, 0, -2],
                        },
                        {
                          text: "____________________",
                          font: "KanitLM",
                          style: "footerSignature",
                          alignment: "center",
                          margin: [0, -10, 0, 0],
                        },
                      ],
                      width: "*",
                    },

                    {
                      text: "ผู้รับเงิน/Cashier",
                      font: "KanitLM",
                      style: "footerSignature",
                      width: "auto",
                      margin: [0, -20, 0, 0],
                    },
                  ],

                  columnGap: 0,
                  border: [false, false, false, false],
                },

                meHealthDivision.includes(props.division || "")
                  ? {
                      text: `(          ในนามบริษัท เวนเจอร์ คะตาลิสต์ จำกัด        )`,
                      font: "KanitLM",
                      style: "footerSignature",
                      alignment: "center",
                      margin: [0, -5, 0, 0],
                    }
                  : {
                      text: `(          ${props.cashier}        )`,
                      font: "KanitLM",
                      style: "footerSignature",
                      alignment: "center",
                      margin: [0, -5, 0, 0],
                    },
              ],
              margin: [0, 20, 0, 0],
              border: [false, false, false, false],
            },
          ],
        ],
      },
    },
  ];
};
